import { DateTimePicker, setDateWithTimezone } from '@systemeio/components'
import { CurrencyEnum, Units } from '@systemeio/types'
import {
  CouponCreateInterface,
  CouponErrorsInterface,
  DiscountTypeInterface,
} from 'modules/coupons/types/coupon-interface'
import { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import FormInputNumberGroupRhf from 'shared/components/form-input-number-group-rhf'
import FormInputRhf from 'shared/components/form-input-rhf'
import FormSelectRhf from 'shared/components/form-select-rhf'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { FieldLabel } from 'shared/components/form/field-label'
import { PillRadioGroup } from 'shared/components/pill-radio-group'
import { useCurrencyData } from 'shared/hooks/use-currency-data'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { filterPassedTime } from 'shared/utils/date-time-format'
import { twJoin } from 'tailwind-merge'

const MIN_PERCENT = 1
const MAX_PERCENT = 100
const MIN_FIXED_AMOUNT = 0.01

interface CouponFormValues {
  discountType: DiscountTypeInterface['id']
  innerName: string
  code: string
  discountAmount: number
  currency?: CurrencyEnum
  expirationDate: string | null
  limitOfUse: string
}

const DEFAULT_VALUES: CouponFormValues = {
  discountType: 'percent',
  innerName: '',
  code: '',
  discountAmount: 0,
  currency: undefined,
  expirationDate: null,
  limitOfUse: '',
}

interface CouponFormProps {
  /** When provided the currency select is hidden and this currency is submitted instead. */
  currency?: CurrencyEnum
  /** Seeds the form, so a choice the user already made outside it is not lost. */
  defaultValues?: Partial<CouponFormValues>
  fieldErrors?: CouponErrorsInterface
  onSubmit: (data: CouponCreateInterface) => Promise<void>
}

function CouponForm({ currency, defaultValues, fieldErrors, onSubmit }: CouponFormProps) {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const currencyData = useCurrencyData()
  const discountTypeOptions: { id: DiscountTypeInterface['id']; caption: string }[] = [
    { id: 'percent', caption: t('dashboard.coupon.discount_type.percent') },
    { id: 'fixed', caption: t('dashboard.coupon.discount_type.fixed') },
  ]

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CouponFormValues>({ defaultValues: { ...DEFAULT_VALUES, ...defaultValues } })

  const discountType = watch('discountType')
  const selectedCurrency = watch('currency')
  const isFixed = discountType === 'fixed'
  const needCurrencySelect = currency === undefined && isFixed

  useEffect(() => {
    if (!fieldErrors) return
    ;(Object.keys(fieldErrors) as (keyof CouponErrorsInterface)[]).forEach(field => {
      const message = fieldErrors[field]
      const name = field === 'common' ? 'root' : field
      if (message) setError(name, { type: 'server', message })
    })
  }, [fieldErrors, setError])

  const handleFormSubmit: SubmitHandler<CouponFormValues> = async values => {
    await onSubmit({
      innerName: values.innerName,
      code: values.code,
      discountType: values.discountType,
      discountAmount: values.discountAmount as Units,
      currency: isFixed ? (currency ?? values.currency) : undefined,
      expirationDate: values.expirationDate,
      limitOfUse: values.limitOfUse === '' ? null : Number(values.limitOfUse),
    } as CouponCreateInterface)
  }

  return (
    <form
      id={CouponForm.formId}
      className="flex flex-col gap-5"
      onSubmit={event => {
        // FormInputNumber commits its value on blur, so flush the focused field first.
        document.activeElement instanceof HTMLElement && document.activeElement.blur()
        handleSubmit(handleFormSubmit)(event)
      }}
    >
      <div className="flex flex-col gap-2">
        <FieldLabel label={t('dashboard.coupon.discount_type.title')} required />
        <Controller
          control={control}
          name="discountType"
          render={({ field }) => (
            <PillRadioGroup
              options={discountTypeOptions}
              onChange={value => {
                field.onChange(value)
                if (value === 'percent') setValue('currency', undefined)
              }}
              value={field.value}
            />
          )}
        />
        <FieldErrorAndDescription error={errors.discountType?.message} />
      </div>
      <FormInputRhf
        control={control}
        name="innerName"
        setValue={setValue}
        rules={{ required: t('dashboard.validation.empty_error.title') }}
        label={t('dashboard.coupon.coupon_name')}
        placeholder={t('global.name')}
        maxLength={256}
        error={errors.innerName?.message}
        required
      />
      <FormInputRhf
        control={control}
        name="code"
        setValue={setValue}
        rules={{ required: t('dashboard.validation.empty_error.title') }}
        label={t('dashboard.coupon.coupon_code')}
        placeholder={t('dashboard.coupon.coupon_code')}
        maxLength={256}
        error={errors.code?.message}
        required
      />
      <div className={twJoin('grid gap-5', needCurrencySelect && 'tablet:grid-cols-2')}>
        <FormInputNumberGroupRhf
          control={control}
          name="discountAmount"
          setValue={setValue}
          rules={{
            required: t('dashboard.validation.empty_error.title'),
            min: {
              value: isFixed ? MIN_FIXED_AMOUNT : MIN_PERCENT,
              message: t('dashboard.validation.empty_error.title'),
            },
          }}
          label={t('dashboard.coupon.discount_amount')}
          min={0}
          {...(isFixed
            ? { toFixed: 2, suffix: (currency ?? selectedCurrency)?.toUpperCase() }
            : { max: MAX_PERCENT, type: 'integer' as const, suffix: '%' })}
          error={errors.discountAmount?.message}
          required
        />
        {needCurrencySelect && (
          <FormSelectRhf
            control={control}
            name="currency"
            setValue={setValue}
            rules={{ required: t('dashboard.validation.empty_error.title') }}
            data={currencyData}
            label={t('dashboard.coupon.currency')}
            placeholder={t('dashboard.coupon.currency_select')}
            error={errors.currency?.message}
            required
          />
        )}
      </div>
      <div className="tablet:grid-cols-2 grid gap-5">
        <Controller
          control={control}
          name="expirationDate"
          render={({ field }) => (
            <DateTimePicker
              clearable
              error={errors.expirationDate?.message}
              intlLocale={intlLocale}
              label={t('dashboard.coupon.expiration_date')}
              minDate={setDateWithTimezone('', user?.timezone)}
              filterTime={time => filterPassedTime(time, user?.timezone)}
              onChange={field.onChange}
              timezone={user?.timezone}
              userDateFormat={user?.dateFormat}
              userTimeFormat={user?.timeFormat}
              value={field.value}
            />
          )}
        />
        <FormInputRhf
          control={control}
          name="limitOfUse"
          setValue={setValue}
          rules={{
            min: { value: 1, message: t('dashboard.validation.empty_error.title') },
          }}
          label={t('dashboard.coupon.limit_of_use')}
          description={t('dashboard.coupon.leave_blank')}
          type="number"
          min={1}
          error={errors.limitOfUse?.message}
        />
      </div>
      <FieldErrorAndDescription error={errors.root?.message} errorClassName="text-center" />
    </form>
  )
}

CouponForm.formId = 'coupon-form'

export { CouponForm }
export type { CouponFormProps }
